import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useFavorites } from '../context/FavoritesContext';
import PlaceCard from './common/PlaceCard';
import FavoritesFilter from './common/FavoritesFilter';
import NotificationSettings from './common/NotificationSettings';
import usePullToRefresh from '../hooks/usePullToRefresh';
import { startPopularityChecker, stopPopularityChecker } from '../services/popularityChecker';

const Favorites = () => {
  const { favorites, removeFavorite, refreshFavorites } = useFavorites();

  const handleRefresh = useCallback(async () => {
    await refreshFavorites();
  }, [refreshFavorites]);

  const { containerRef, pullDistance, refreshing } = usePullToRefresh(handleRefresh);

  useEffect(() => {
    startPopularityChecker();
    return () => stopPopularityChecker();
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');

  const filteredAndSortedFavorites = useMemo(() => {
    let result = [...favorites];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (place) =>
          place.name.toLowerCase().includes(query) || place.address.toLowerCase().includes(query)
      );
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'popularity':
        case 'popularity-asc': {
          const aValue =
            a.business_status === 'CLOSED_TEMPORARILY' ? -1 : (a.current_popularity ?? -1);
          const bValue =
            b.business_status === 'CLOSED_TEMPORARILY' ? -1 : (b.current_popularity ?? -1);
          const comparison = aValue - bValue;
          return sortBy === 'popularity' ? -comparison : comparison;
        }
        default:
          return 0;
      }
    });

    return result;
  }, [favorites, searchQuery, sortBy]);

  return (
    <div ref={containerRef} className="space-y-5">
      {/* Pull-to-refresh indicator */}
      {(pullDistance > 0 || refreshing) && (
        <div
          className="flex justify-center overflow-hidden transition-all"
          style={{ height: refreshing ? 40 : pullDistance * 0.5 }}
        >
          <svg
            className={`w-5 h-5 text-honey-500 ${refreshing ? 'animate-spin' : ''}`}
            style={{ opacity: Math.min(pullDistance / 80, 1) }}
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        </div>
      )}

      {favorites.length > 0 && (
        <FavoritesFilter
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          onSortChange={setSortBy}
          count={favorites.length}
        />
      )}

      {filteredAndSortedFavorites.length === 0 && searchQuery && (
        <div className="flex flex-col items-center justify-center py-12 text-center animate-fade-in">
          <div className="w-14 h-14 rounded-full bg-gray-50 dark:bg-gray-700/50 flex items-center justify-center mb-4">
            <svg
              className="w-7 h-7 text-gray-300 dark:text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500">
            No favorites match &ldquo;{searchQuery}&rdquo;
          </p>
        </div>
      )}

      {filteredAndSortedFavorites.length === 0 && !searchQuery && (
        <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-in">
          <div className="w-20 h-20 rounded-full bg-honey-50 dark:bg-honey-900/20 flex items-center justify-center mb-5">
            <svg
              className="w-10 h-10 text-honey-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm font-medium mb-1">
            No favorites yet
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-xs font-light">
            Search for places and save the ones you love
          </p>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {filteredAndSortedFavorites.map((place, index) => (
          <div
            key={place.place_id}
            className="animate-slide-up"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <PlaceCard
              place={place}
              buttonText="Remove"
              onButtonClick={() => removeFavorite(place)}
              disabled={false}
            />
          </div>
        ))}
      </div>

      {/* Notification Settings */}
      <div className="mt-6">
        <NotificationSettings />
      </div>
    </div>
  );
};

export default Favorites;
